/**
 * Migration utility to move existing access codes from hardcoded array to Supabase
 * This should be run once to migrate existing codes
 */

import { ACCESS_CODES } from './accessCodes';
import accessCodeService from '../services/accessCodeService';

/**
 * Migrate access codes from hardcoded array to Supabase
 * @returns {Promise<{success: number, failed: number, errors: Array}>}
 */
export const migrateAccessCodes = async () => {
  const results = {
    success: 0,
    failed: 0,
    errors: [],
  };

  // Check if migration has already been done
  const migrationKey = 'access_codes_migrated';
  const alreadyMigrated = localStorage.getItem(migrationKey);
  
  if (alreadyMigrated === 'true') {
    console.log('Access codes already migrated. Skipping migration.');
    return results;
  }

  console.log('Starting access code migration...');

  for (const accessCode of ACCESS_CODES) {
    try {
      const code = typeof accessCode === 'string' ? accessCode : accessCode.code;
      const expiresAt = typeof accessCode === 'object' && accessCode.expiresAt 
        ? accessCode.expiresAt 
        : null;
      const maxValuations = typeof accessCode === 'object' && accessCode.maxValuations
        ? accessCode.maxValuations
        : null;

      // Check if code already exists
      const existing = await accessCodeService.getAccessCodeByCode(code);
      
      if (existing) {
        console.log(`Access code "${code}" already exists in database. Skipping.`);
        results.success++;
        continue;
      }

      // Create the access code
      await accessCodeService.createAccessCode({
        code,
        expires_at: expiresAt,
        max_valuations: maxValuations,
        is_active: true,
      });

      console.log(`Migrated access code: ${code}`);
      results.success++;
    } catch (error) {
      console.error(`Failed to migrate access code:`, error);
      results.failed++;
      results.errors.push({
        code: typeof accessCode === 'string' ? accessCode : accessCode.code,
        error: error.message,
      });
    }
  }

  // Mark migration as complete
  if (results.failed === 0) {
    try {
      localStorage.setItem(migrationKey, 'true');
      console.log('Migration completed successfully!');
    } catch (error) {
      console.warn('Failed to mark migration as complete:', error);
    }
  }

  return results;
};

/**
 * Check if migration is needed
 * @returns {Promise<boolean>}
 */
export const needsMigration = async () => {
  const migrationKey = 'access_codes_migrated';
  const alreadyMigrated = localStorage.getItem(migrationKey);
  
  if (alreadyMigrated === 'true') {
    return false;
  }

  // Check if any codes exist in database
  try {
    const codes = await accessCodeService.getAllAccessCodes({ includeInactive: true });
    return codes.length === 0;
  } catch (error) {
    console.error('Error checking migration status:', error);
    return true; // Assume migration needed if check fails
  }
};

export default migrateAccessCodes;

